import { GridApiCommunity } from "@mui/x-data-grid/internals";
import { GridRowId } from "@mui/x-data-grid";
import { MappedIcons } from "../../components/icon-factory/icon-factory";
import { BiqlQueryResponseMetadata } from "../../qubes-client/protocol/biql-query.protocol";
import {
  BiqlFilter,
  RowAction,
  TableModel,
} from "../../components/qubes-table/table-model";
import { BiqlQuery } from "../../qubes-client/api";
import { Duration } from "../../duration";
import { FieldDef } from "./field";
import type { OptionalColumnsFallback } from "../../components/qubes-table/table-model";

export type Page = {
  name: string;
  views: View[];
};

export type View = BiqlView | TableModelView | SqlFirewallView;

export type ViewID = string;

type BaseView = {
  name: string;
  kind: "biql" | "tableModel" | "sql-firewall";
  /** identifier for this view instance — useful for subscriptions */
  id: ViewID;
  /** enable multiple row selection in the UI for this view */
  multiSelect?: boolean;
  fields?: FieldDef[];
};

export type TableModelView = BaseView & {
  kind: "tableModel";
  tableModel: TableModel<Record<string, unknown>>;
};

export type BiqlView = BaseView & {
  kind: "biql";
  biqlQuery: BiqlQuery;
  actions?: CubeAction[];
  rowActions?: RowAction<Record<string, unknown>>[];
  filters?: BiqlFilter[];
  toolbarActions?: ToolbarAction[];
};

export type ToolbarAction = {
  name: string;
  icon: MappedIcons;
  run: () => void;
  /** a dropdown: the button opens these instead of running `run` */
  children?: Array<{ name: string; description?: string; run: () => void }>;
  // Optional role gate — when set, the action is only rendered if the
  // currently-logged-in user has this role. Filtered out in QubesView
  // via a Redux subscription so the role is evaluated at render time
  // rather than at view-construction time (which can run before the
  // user is hydrated into the store).
  requiresRole?: string;
};

export type SqlFirewallView = BaseView & {
  kind: "sql-firewall";
  /** the query; `where` conditions from drill-down filters are appended */
  sql?: string;
  /** `sql` without the view's optional columns, run when the live model lacks one */
  optionalFallback?: OptionalColumnsFallback;
  /**
   * Instead of `sql`: produce the rows yourself, given the satisfied filters'
   * where-clauses (the page DSL's function source — e.g. a query plus a
   * second per-row lookup). Every row must carry `uid`.
   */
  fetch?: (where: string[]) => Promise<Record<string, unknown>[]>;
  /** called after a row is selected (e.g. navigate to a detail page) */
  onRowClick?: (row: Record<string, unknown>) => void;
  /**
   * Row identity, when `uid` alone is not one row: the named columns, joined.
   * A join that fans out (a monitor with two jobs) needs the joined key too.
   */
  rowKey?: string[];
  /** initial sort, applied when the view mounts */
  defaultSort?: { field: string; direction: "asc" | "desc" };
  rowActions?: RowAction<Record<string, unknown>>[];
  filters?: BiqlFilter[];
  cdc?: { database: string; table: string };
  refreshInterval?: Duration;
  toolbarActions?: ToolbarAction[];
  /**
   * "record" draws ONE row as a labelled card instead of a grid, with `groups`
   * splitting it into sections (FEATURE-20260831-more-dsls-form-and-record-page).
   * "form" draws the `context` fields as input controls; each field's value is
   * published to ViewManager as the selection of the pseudo-view
   * `contextViewId(view.id, field.name)`, so dependent views filter by it
   * (FEATURE-20261014-page-dsl-reference-field-is-picker-or-filter).
   */
  layout?: "grid" | "record" | "form";
  groups?: Array<{ title: string; fields: string[] }>;
  context?: ContextFieldDef[];
  /**
   * Dynamic title: reflects the current drill-down scope (e.g.
   * "Fields · mock_erp.ap_payment"). Re-evaluated on every selection change.
   * Falls back to `name` when absent.
   */
  title?: () => string;
  /**
   * Empty-state message shown when the grid has no rows — used to say WHY
   * (e.g. "Select a table to see its fields") instead of a bare "No rows".
   * Re-evaluated on selection change.
   */
  emptyMessage?: () => string;
};

/** One control of a form-layout view; see SqlFirewallView.context. */
export type ContextFieldDef = {
  /** the value's column on the owner table, and the form value's name */
  name: string;
  label: string;
  type: "string" | "boolean" | "number" | "timestamp" | "json";
  /** the route query parameter the value travels in */
  param: string;
  /** a reference field: a picker over `target`, read by `display`, valued by `targetKey` */
  reference?: { owner: string; path: string; target: string; targetKey: string; display: string; columns: string[] };
};

/** The ViewManager id a form-layout view publishes one field's value under. */
export function contextViewId(viewId: ViewID, fieldName: string): ViewID {
  return `${viewId}:${fieldName}`;
}

export type QubesView = {
  /** unique identifier for this QubesView instance (required for slice tracking) */
  id: ViewID;
  view: View;
};

export type ViewDependency = {
  label?: string;
  dependsOn: ViewID;
  onDependencySelectionChanged: (
    filter: BiqlFilter,
    api: GridApiCommunity,
    dependencyApi: GridApiCommunity,
    rowIds: GridRowId[],
    columnField?: string,
  ) => void;
  onDependencyFilterChanged?: (
    api: GridApiCommunity,
    filters: Array<{ field: string; value: unknown }>,
    view?: View,
  ) => void;
};

export type CubeAction = {
  name: string;
  icon: MappedIcons;
  fcn: (
    api: GridApiCommunity,
    row: Record<string, unknown>,
    view?: View,
    metadata?: BiqlQueryResponseMetadata[],
    rowIndex?: number,
    colIndex?: number,
  ) => void;
};

/**
 * Returns true if the given view is a BiqlView
 */
export function isBiqlView(view: View): view is BiqlView {
  return view.kind === "biql";
}

/**
 * Returns true if the given view is a TableModelView
 */
export function isTableModelView(view: View): view is TableModelView {
  return view.kind === "tableModel";
}

export function isSqlFirewallView(view: View): view is SqlFirewallView {
  return view.kind === "sql-firewall";
}
